import { useCallback, useEffect, useRef, useState } from 'react';
import { isAuthStatus, type AuthRevalidationFn } from '../lib/auth';
import {
  fetchSymbolUniverse,
  SymbolUniverseRequestError,
  type SymbolUniverse,
} from '../lib/symbols';

const RETRY_BASE_DELAY_MS = 1_000;
const RETRY_MAX_DELAY_MS = 30_000;

interface UseSymbolUniverseResult {
  symbolUniverse: SymbolUniverse | null;
  initialLoadError: string | null;
  refreshSymbolUniverse: () => Promise<void>;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Failed to load symbols';
}

export function useSymbolUniverse(
  onAuthRevalidationRequested: AuthRevalidationFn,
  onSymbolUniverseLoaded?: (universe: SymbolUniverse) => void,
): UseSymbolUniverseResult {
  const [symbolUniverse, setSymbolUniverse] = useState<SymbolUniverse | null>(null);
  const [initialLoadError, setInitialLoadError] = useState<string | null>(null);
  const symbolUniverseRef = useRef<SymbolUniverse | null>(null);
  const retryAttemptRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const refreshPendingRef = useRef(false);
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  const authRevalidationRef = useRef(onAuthRevalidationRequested);
  const onSymbolUniverseLoadedRef = useRef(onSymbolUniverseLoaded);
  const isMountedRef = useRef(false);

  useEffect(() => {
    authRevalidationRef.current = onAuthRevalidationRequested;
  }, [onAuthRevalidationRequested]);

  useEffect(() => {
    onSymbolUniverseLoadedRef.current = onSymbolUniverseLoaded;
  }, [onSymbolUniverseLoaded]);

  const clearRetry = useCallback(() => {
    if (retryTimeoutRef.current != null) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const scheduleRetry = useCallback(() => {
    if (!isMountedRef.current || retryTimeoutRef.current != null) {
      return;
    }

    const delay = Math.min(RETRY_BASE_DELAY_MS * 2 ** retryAttemptRef.current, RETRY_MAX_DELAY_MS);
    retryAttemptRef.current += 1;
    retryTimeoutRef.current = setTimeout(() => {
      retryTimeoutRef.current = null;
      void refreshRef.current();
    }, delay);
  }, []);

  const refreshSymbolUniverse = useCallback((): Promise<void> => {
    if (refreshInFlightRef.current != null) {
      refreshPendingRef.current = true;
      return refreshInFlightRef.current;
    }

    const request = (async () => {
      do {
        refreshPendingRef.current = false;
        try {
          const universe = await fetchSymbolUniverse();
          if (!isMountedRef.current) {
            return;
          }

          clearRetry();
          retryAttemptRef.current = 0;
          symbolUniverseRef.current = universe;
          setSymbolUniverse(universe);
          setInitialLoadError(null);
          onSymbolUniverseLoadedRef.current?.(universe);
        } catch (error) {
          if (!isMountedRef.current) {
            return;
          }

          if (symbolUniverseRef.current == null) {
            setInitialLoadError(getErrorMessage(error));
          }
          scheduleRetry();
          if (error instanceof SymbolUniverseRequestError && isAuthStatus(error.status)) {
            void authRevalidationRef.current('background').catch((authError: unknown) => {
              console.error('[symbols] auth revalidation failed', authError);
            });
          }
        }
      } while (refreshPendingRef.current);
    })();

    refreshInFlightRef.current = request;
    void request.finally(() => {
      if (refreshInFlightRef.current === request) {
        refreshInFlightRef.current = null;
      }
    });
    return request;
  }, [clearRetry, scheduleRetry]);

  useEffect(() => {
    refreshRef.current = refreshSymbolUniverse;
  }, [refreshSymbolUniverse]);

  useEffect(() => {
    isMountedRef.current = true;
    void refreshSymbolUniverse();

    return () => {
      isMountedRef.current = false;
      clearRetry();
    };
  }, [clearRetry, refreshSymbolUniverse]);

  return {
    symbolUniverse,
    initialLoadError,
    refreshSymbolUniverse,
  };
}
